import Vue from 'vue';
import Vuex from 'vuex';
import axios from 'axios';
import router from '../router/index.js'
const pako = require('pako');

import {
  getTime, getPlayer, getDivider, bombPotInfo
} from './helpers.js';

import { updateBotState, makeChatAccount } from './store_updates';

Vue.use(Vuex);


function secondsBetweenDates(d1, d2) {
  let diff = Math.abs(d1 - d2);
  return Math.floor(diff / 1000);
}


function handlePendingOnAddMessage(pendingMessages, payload, displayID) {
  let msg = payload.data;

  if (!pendingMessages || !pendingMessages.length) return;

  // must be from bot to be pending
  if (msg.displayName !== displayID) return;

  let indexes = pendingMessages.map((e, i) => e.text === msg.text ? i : '').filter(String);
  while (indexes.length) {
    pendingMessages.splice(indexes.pop(), 1)
  }
}


function handlePendingOnBatch(state, batch_ts) {
  let pendingMessages = state.pendingMessages;
  if (!pendingMessages) return;

  let now = new Date();
  let changes = false;

  for (let name in pendingMessages) {
    if (!pendingMessages[name].length) continue;
    let indexes = pendingMessages[name]
      .filter(e => !e.failed && e.status == 'sent') // only get non failed and sent
      .map((e, i) => {

        // if it's too new, ignore
        if (secondsBetweenDates(now, e.status_change_ts) <= 5) return '';

        // if batch sent <= 5 sec after status change, ignore
        try {
          if (Math.abs(e.status_change_server_ts - batch_ts) <= 5) return '';
        } catch (e) {
          console.log(e);
        }

        // not too new and batch sent > 5 sec after status change, mark as failed
        return i;
      }) // only get ones that are old enough
      .filter(String); // only valid indexes
    if (!indexes.length) continue;

    changes = true;
    while (indexes.length) {
      pendingMessages[name][indexes.pop()].failed = true;
    }
  }

  if (!changes) return;

  // force update
  state.pendingMessages = {...pendingMessages};
}


function addMessage(seenMessageIds, messages, payload) {
  if (seenMessageIds[payload._id]) {
    // already seen, nothing to do
    return false;
  }

  let msg = payload.data;
  let server_ts = payload.server_ts;
  let ts = new Date();
  if (server_ts) {
    ts = new Date(server_ts * 1000)
  }

  let read = Boolean(msg.read);
  messages.push({
    'type': payload.type || 'group',
    'ts': ts,
    'text': msg.text,
    'name': msg.displayName,
    'senderId': msg.senderId,
    'senderType': msg.senderType,
    'read': read,
    'dev_only': msg.dev_only,
    'is_debug': msg.is_debug,

    // voice chat
    'filename': msg.filename,

    // gift chat
    'giftId': msg.giftId,
    'targetName': msg.targetName,
    'targetIsBot': msg.targetIsBot,

    // emoji chat
    'emojiId': msg.emojiId,

    'name_mentions': msg.name_mentions,
    'result': msg.result || 0,
    'senderName': msg.sent_by ? msg.sent_by.user : null,
    'hand_num': payload.type === "handend" ? msg.hand_num || -1 : -1,
    'group': msg.group !== false,
    '_id': payload._id
  });
  seenMessageIds[payload._id] = true;
  return true;
}

function addWarningMessage(state, msg) {
  let last = {'displayID': '', 'msg': ''}
  if (state.warnings.length > 0) {
    last = state.warnings[0];
  }
  if (last.displayID === msg.displayID && last.msg === msg.msg) {
    // no need for dups
    return;
  }
  state.warnings.unshift(msg);
}

function accountPriorityGroup(state, chatAcc) {
  // first ones that you claimed
  if (chatAcc.claimed && state.user && state.user._id === chatAcc.claimed_user_id) {
    return 0;
  }

  // then you contacting
  if (chatAcc.contacting_host && chatAcc.contacting_host_users &&
    (state.user._id in chatAcc.contacting_host_users) &&
    chatAcc.contacting_host_users[state.user._id].action === 'handling') {
    return 500;
  }

  // then you handling buyin rejected
  if (chatAcc.buyin_state && chatAcc.buyin_state.type === 'rejected' && chatAcc.buyin_state.contacting_users &&
    (state.user._id in chatAcc.buyin_state.contacting_users) &&
    chatAcc.buyin_state.contacting_users[state.user._id].action === 'handling') {
    return 600;
  }

  // then priming
  if (chatAcc.priming) {
    return 1000;
  }

  // then snoozed
  if (chatAcc.snoozed) {
    return 2000;
  }

  // now from the bottom of the list:
  // someone else claimed
  if (chatAcc.claimed) {
    return 60000;
  }
  // someone else contacting host
  if (chatAcc.contacting_host) {
    return 70000;
  }

  // someone else handling buyin rejected
  if (chatAcc.buyin_state && chatAcc.buyin_state.type === 'rejected') {
    return 80000;
  }

  // ignore chats?
  let as = chatAcc.automationState;
  if (as && as.ignore_chats) {
      return 90000;
  }

  // then everything 'normal''
  return 50000;
}


function accountPriorityMain(state, chatAcc) {
  let as = chatAcc.automationState;

  // name mention etc
  if (as && as.operator_alerts && as.operator_alerts.length > 0) {
    return 0;
  }

  // chat suggestion
  let s1 = 0;

  // check if there is a chat suggestion
  if (chatAcc.name in state.chatSuggestions2 && state.chatSuggestions2[chatAcc.name].length > 0) {
    s1 = Math.max(state.chatSuggestions2[chatAcc.name].map(el => el.priority || 1));
  }

  // now check if there is a chatbot suggestion
  // but only if user is chatbot_manager
  if (state.user && state.user.groups && state.user.groups.includes("chatbot_manager")) {
    if (chatAcc.name in state.chatbotSuggestions && state.chatbotSuggestions[chatAcc.name].length > 0) {
      s1 = Math.max(s1, Math.max(state.chatbotSuggestions[chatAcc.name].map(el => el.priority || 1)));
    }
  }

  if (s1) {
    return 10000 - s1;  // tie-break on suggestion priority
  }

  if (as) {
    // need to stand
    if (as.stand_now) {
      return 30000;
    }

    // need to give warning
    if (as.give_warning_now) {
      return 40000;
    }
  }

  if (!as) {
    // no auto state, keep eye on these
    return 99998;
  }

  // nothing special
  return 99999;
}


function accountPriorityTuple(state, chatAcc) {
  let as = chatAcc.automationState;

  // lower number = sort to top
  // list, tie-break go to next element
  return [
    // first the group (snoozed, claimed, etc)
    accountPriorityGroup(state, chatAcc),

    // then the type
    accountPriorityMain(state, chatAcc),

    // tie-break on # unreads, more unread is higher up
    -chatAcc.unread,

    // tie-break on whether there is any warning text
    as && as.sidebar_warning_text ? 0 : 1,

    // tie-break on name
    (chatAcc.name || '').toLowerCase(),
  ];
}


function tupleCompare(t1, t2) {
  // compare two lists, first element, tie-break on 2nd, etc
  let index = 0;
  while (index < t1.length && index < t2.length) {
    // we have a common element
    let el1 = t1[index];
    let el2 = t2[index];

    if (el1 < el2) {
      return -1;
    }

    if (el1 > el2) {
      return 1;
    }

    // they are tied, to go next element
    index++;
  }

  // we are at the end, if one is shorter then it goes first
  if (t1.length < t2.length) {
    return -1;
  }

  if (t2.length < t1.length) {
    return 1;
  }

  // fully tied!
  return 0;
}


function needsPing(state) {
  if (state.lastPings.length === 0) {
    // need first ping, but wait for app to fully load first
    return Date.now() - state.startedMs >= 3000;
  }

  let mostRecentMs = state.lastPings[0].sentMs;
  let nowMs = Date.now();
  let msSinceLastPing = nowMs - mostRecentMs;

  // every 3 seconds plz
  return msSinceLastPing >= 3000;
}


// given list of clubmgm accounts for one actual pkr2 id, return the most relevant one based
// on what club is entered
function mostRelevantClubmgmAccount(theseClubmgmAccounts, chatAccountsByKey) {
  if (!theseClubmgmAccounts || theseClubmgmAccounts.length === 0) {
    // no account
    return null;
  }

  // all nicknames should be the same
  let nickname = theseClubmgmAccounts[0].nickname;

  let chatAccKey = nickname;
  if (!chatAccountsByKey[chatAccKey]) {
    // we have no online account, so return arbitrary one
    return theseClubmgmAccounts[0];
  }

  let clubID = chatAccountsByKey[nickname].clubID;
  if (!clubID) {
    // no entered club, return arbitrary one
    return theseClubmgmAccounts[0];
  }

  for (let checkAcc of theseClubmgmAccounts) {
    if ((checkAcc.club || {}).pkr2_id === clubID) {
      // it's in this club!
      return checkAcc;
    }
  }

  // nothing matched, return arbitrary one
  console.warn("acc " + nickname + " has " + (theseClubmgmAccounts.length) + " clubmgm accounts, and entered " +
      "club " + clubID + ", but no clubmgm accounts matched this");
  return theseClubmgmAccounts[0];
}

function handle_chat_msgs(state, name, msgs) {
  let messages = state.messages[name] || [];
  let pendingMessages = state.pendingMessages[name] || [];

  let unread = state.numUnread[name] || 0;
  for (let m of msgs) {
    let added = addMessage(state.seenMessageIds, messages, m);
    handlePendingOnAddMessage(pendingMessages, m, name)

    if (!['warning', 'suggestion', 'game_event', 'handend'].includes(m.type) && !m.data.read && added) {
      unread++;
    }
  }
  state.numUnread[name] = unread;
  state.numUnread = {...state.numUnread};
  state.messages[name] = messages;
  state.messages = {...state.messages};
  state.pendingMessages[name] = pendingMessages;
  state.pendingMessages = {...state.pendingMessages};
  state.chatAccountsLive[name] = makeChatAccount(state, name);
  state.chatAccountsLive = {...state.chatAccountsLive};
}


function nextChatAccount(state, getters) {
  // pick the next chat account

  let duty_type = getters.user.on_duty_type;
  let activeOperatorId = getters.activeOperatorId;

  // get the ordered list of chat accounts
  let availableUnread = getters.chatAccounts.filter(chatAcc => {

    // if secondary and needs_help, ignore
    if (duty_type === 'secondary' && chatAcc.operator_needs_help && chatAcc.operator_needs_help._id) {
      return false;
    }

    // if no unread, can't pick this
    if (chatAcc.unread <= 0) {
      return false;
    }

    // if claimed, only relevant if you claimed it
    if (chatAcc.claimed) {
      return chatAcc.claimed_user_id === activeOperatorId;
    }

    // if priming, only relevant if you're the one priming
    if (chatAcc.priming) {
      return chatAcc.priming_user_id === activeOperatorId;
    }

    // if snoozed, skip it
    if (chatAcc.snoozed) {
      return false;
    }

    // if ignore chats, indeed ignore chats
    let as = chatAcc.automationState;
    if (as && as.ignore_chats) {
      return false;
    }

    return true;
  });


  // pick the most-priority one to do it
  if (availableUnread && availableUnread.length > 0) {
    // if primary get first
    if (duty_type === 'primary') {
      return availableUnread[0].name;
    } else if (duty_type === 'secondary') {
      return availableUnread[availableUnread.length - 1].name;
    } else {
      // fallback to first
      return availableUnread[0].name;
    }
  }

  // nobody next
  return null;
}


const store = new Vuex.Store({
  state: {
    isDisconnected: false,
    subscribedChannels: [],

    userInactive: false,
    debug: false,
    testPreview: false,
    testWarnings: false,
    agents: [],
    identities: [],
    onlineAccounts: {},
    accounts_automation_state: {},  // map displayID to the automation state of that account

    messages: {},
    pendingMessages: {},

    seenMessageIds: {}, // map message ids we seen so we don't duplicate stuff
    states: {},
    numUnread: {},
    numUnreadTg: {},
    numUnreadSMS: {},
    markerTs: {},
    activeChatAccountName: null,

    // full list of clubmgm accounts -- list of entries for each account, one for each
    // club they are in
    clubmgmAccountsAll: {
      byNickname: {},
      byId: {},
    },

    clubs: {},
    rooms: {},
    users: {},  // map mongo id to user info like name
    warnings: [],
    schedulerGroups: {},
    usersOnDuty: [],
    user: null,
    canAutoFocus: true,
    pokerHandHistory: {},
    roomQueue: {},

    // -----------------------

    chatAccountsLive: {},  // live-updated, front-end shouldn't use this!
    // cached ones, all the getters refer to these so we don't have endless update loops
    sortedChatAccountsCache: {
      recomputeNow: false,
      recomputeCheckSeconds: 0,
      chatAccounts: [],
      chatAccountsByKey: {},
    },

    // -----------------------

    chatSuggestions2: {},
    chatbotSuggestions: {},
    opponents: {},
    playerActionsKey: 0,

    // pings
    lastPings: [],  // example: {id: 0, sentMs: 1031301031, responseMs: 032033}
    startedMs: Date.now(),
    userPingData: [],

    nBotStateBatchesProcessing: 0,

    // track when we got a new account, so within 5 seconds of last new account gotten
    // we can do the dispatch
    lastNewAccountMs: 0,
  },
  getters: {
    // ! AUTH
    isAuthenticated(state) {
      // console.log(state.user);
      if (!state.user || !state.user.user || state.user.user.length <= 0) {
        // console.log("not authenticated?")
        return false;
      }
      // console.log("yes authenticated")
      return true;
    },
    isClaud(state) {
      if (!state.user || !state.user.user || state.user.user.length <= 0) {
        return false;
      }

      return state.user.user === 'claudiu';
    },
    isAlex(state) {
      if (!state.user || !state.user.user || state.user.user.length <= 0) {
        return false;
      }

      return state.user.user === 'alex';
    },
    isPedro(state) {
      if (!state.user || !state.user.user || state.user.user.length <= 0) {
        return false;
      }

      return state.user.user === 'pborges';
    },
    isDev(state) {
      if (!state.user || !state.user.user || state.user.user.length <= 0) {
        return false;
      }

      if (!state.user.groups) return false;

      return state.user.groups.includes("developer");
    },
    isSimple(state) {
      // default to true until is able to check, so simple user don't get stuff it can't see
      if (!state.user || !state.user.user || state.user.user.length <= 0) {
        return true;
      }

      if (!state.user.groups) return true;

      return state.user.groups.includes("simple");
    },
    isLimited(state) {
      // default to true until is able to check, so limited user don't get stuff it can't see
      if (!state.user || !state.user.user || state.user.user.length <= 0) {
        return true;
      }

      if (!state.user.groups) return true;

      return state.user.groups.includes("limited");
    },
    isAdmin(state) {
      if (!state.user || !state.user.user || state.user.user.length <= 0) {
        return false;
      }

      if (!state.user.groups) return false;

      return state.user.groups.includes("admin");
    },
    isManager(state) {
      if (!state.user || !state.user.user || state.user.user.length <= 0) {
        return false;
      }

      if (!state.user.groups) return false;

      return state.user.groups.includes("manager");
    },
    isChatManager(state) {
      if (!state.user || !state.user.user || state.user.user.length <= 0) {
        return false;
      }

      if (!state.user.groups) return false;

      return state.user.groups.includes("chat_manager");
    },
    isChatbotManager(state) {
      if (!state.user || !state.user.user || state.user.user.length <= 0) {
        return false;
      }

      if (!state.user.groups) return false;

      return state.user.groups.includes("chatbot_manager");
    },
    isMiner(state) {
      if (!state.user || !state.user.user || state.user.user.length <= 0) {
        return false;
      }

      if (!state.user.groups) return false;

      return state.user.groups.includes("miner");
    },
    isDebug(state) {
      return state.debug;
    },

    activeOperatorId(state) {
      if (!state.user) { return null; }
      return state.user._id;
    },
    operatorUserName: (state) => (userId) => {
      if (!(userId in state.users)) return '?';
      return state.users[userId].user;
    },
    roomQueue: (state) => {
      return state.roomQueue;
    },

    isOursByPlayerID: (state) => (playerID) => {
      // its ours if we have a clubmgm account for it
      let accs = state.clubmgmAccountsAll.byId[playerID];
      if (accs && accs.length && accs.length > 0) {
        return true;
      }

      return false;
    },
    clubmgmAccountByName: (state) => (name) => {
      let accs = state.clubmgmAccountsAll.byNickname[name];
      let chatAccountsByKey = state.sortedChatAccountsCache.chatAccountsByKey;
      return mostRelevantClubmgmAccount(accs, chatAccountsByKey);
    },
    activeChatAccountName: (state) => {
      // make a dependency on this key so the getter re-renders
      if (state.playerActionsKey) {
        let foolMe1 = 0;
        let foolMe2 = 0;
        foolMe1 = foolMe2;
        foolMe2 = foolMe1
      }

      return state.activeChatAccountName;
    },
    activeChatAccount: (state, getters) => {
      return getters.chatAccountByKey(getters.activeChatAccountName);
    },
    activeClubmgmAccount: (state, getters) => {
      return getters.clubmgmAccountByName(state.activeChatAccountName);
    },
    activeClubmgmClub: (state, getters) => {
      let acc = getters.activeClubmgmAccount;

      if (!acc) {
        return {};
      }

      return acc.club;
    },
    automationStateFor: (state) => (name) => {
      // let key = name;
      // let chatAccount = getters.chatAccountByKey(key);

      // use the live one!
      let chatAccount = state.chatAccountsLive[name];

      if (!chatAccount) {
        return null;
      }
      return chatAccount.automationState;
    },
    activeAutomationState: (state, getters) => {
      return getters.activeChatAccount.automationState;
    },
    agents(state) {
      return state.agents;
    },
    chatSuggestions2: (state) => (name) => {
      if (!(name in state.chatSuggestions2)) return [];
      return state.chatSuggestions2[name];
    },
    chatbotSuggestions: (state) => (name) => {
      if (!(name in state.chatbotSuggestions)) return [];
      return state.chatbotSuggestions[name];
    },
    opponents(state) {
      return state.opponents;
    },
    chatAccounts(state, getters) {
      // do we use cache?
      if (state.sortedChatAccountsCache.recomputeNow) {
        console.log("recomputing chats now");
      } else {
        if (state.sortedChatAccountsCache.recomputeCheckSeconds < 5) {
          return state.sortedChatAccountsCache.chatAccounts;
        }
        console.log("recomputing chats from check seconds passed");
      }

      // compute the list
      let accs = state.onlineAccounts;
      let res = [];
      for (let name in accs) {
        let acc = accs[name];
        if (acc.status !== 'in game') {
          continue;
        }
        res.push(state.chatAccountsLive[name]);
      }

      // sort the list
      res.sort((a1, a2) => {
        return tupleCompare(accountPriorityTuple(state, a1), accountPriorityTuple(state, a2));
      });

      if (res.length === 0) {
        state.activeChatAccountName = null;
      }

      let result = res;

      // deep-copy result so that nothing live-updates
      result = JSON.parse(JSON.stringify(result));

      // freeze it, cache is read-only
      Object.freeze(result);

      // arrange by key as well
      let resultByKey = {};
      for (let chatAcc of result) {
        let key = chatAcc.name;
        resultByKey[key] = chatAcc;
      }
      // freeze this also
      Object.freeze(resultByKey);

      // update result
      state.sortedChatAccountsCache.recomputeNow = false;
      state.sortedChatAccountsCache.recomputeCheckSeconds = 0;  // clear compute check
      state.sortedChatAccountsCache.chatAccountsByKey = resultByKey;
      state.sortedChatAccountsCache.chatAccounts = result;

      console.log("new chat accs by key:", resultByKey);

      // trigger re-compute of the middle
      state.playerActionsKey++;

      // trigger pick chat account if we dont have one
      if (!state.activeChatAccountName) {
        console.log("auto-pick from recompute side-bar with no active account");
        state.activeChatAccountName = nextChatAccount(state, getters);
      }

      return result;
    },
    chatAccountByKey: (state) => (key) => {
      // cached one!

      return state.sortedChatAccountsCache.chatAccountsByKey[key];
    },
    schedulerGroups: (state) => {
      return state.schedulerGroups;
    },

    pendingMessagesForUser: (state) => (name) => {
      if (!(name in state.pendingMessages)) {
        return [];
      }
      return state.pendingMessages[name];
    },
    messagesForUser: (state) => (name) => {
      if (!(name in state.messages)) {
        return [];
      }
      return state.messages[name];
    },

    unreadMessages: (state) => (name) => {
      if (name in state.numUnread)
        return state.numUnread[name];
      return 0;
    },
    markers: (state) => (name) => {
      if (name in state.markerTs) {
        return state.markerTs[name];
      }
      return null;
    },
    states: (state) => {
      return state.states;
    },
    accountState: (state) => (name) => {
      return state.states[name];
    },
    gameType: (state) => (name) => {
      let acc = state.chatAccountsLive[name];
      return acc ? acc.gameType : '';
    },
    bombPotInfo: (state) => (name) => {
      let st = state.states[name];
      return bombPotInfo(st);
    },
    result: (state) => (name) => {
      let st = state.states[name];
      if (!st) {
        return 0;
      }
      let p = getPlayer(st);
      if (!p) return 0;
      let d = getDivider(st);
      // if (p && p.result) return (p.result / d).toFixed(2);
      if (st && st.room && st.room.ranking) {
        for (let r of st.room.ranking) {
          if (r.playerId === p.id) {
            if (!r.diff) {
              // happens in tournaments
              return 0;
            }
            return (r.diff / d).toFixed(2)
          }
        }
      }
      return 0;
    },
    ourPlayer: (state) => (name) => {
      let st = state.states[name];
      if (!st) return null;
      let pid = st.player_id;
      var k;
      if (st.p1) {
        // console.log("this is ofc")
        for (k of ["p1", "p2", "p3"]) {
          let p = st[k];
          //console.log(p.id, pid)
          if (p && p.id === pid) {
            return p
          }
        }
      } else {
        for (k in st.players) {
          let p = st.players[k];
          if (p && p.id === pid) {
            return p
          }
        }
      }
      return null;
    },
    clubs: (state) => (name) => {
      if (!(name in state.clubs)) {
        return [];
      }
      return state.clubs[name];
    },
    rooms: (state) => (name) => {
      if (!(name in state.rooms)) {
        return [];
      }
      return state.rooms[name];
    },
    warnings(state) {
      return state.warnings;
    },
    usersOnDuty(state) {
      return state.usersOnDuty;
    },
    user(state) {
      return state.user;
    },
    onDuty(state) {
      if (!state.user) return false;
      return state.user.on_duty;
    },
    pokerHandHistory: (state) => (name) => {
      let hh = state.pokerHandHistory[name];
      if (!hh) {
        return null;
      }
      return hh;
    },
    nPingsWaiting(state) {
      let res = 0;
      state.lastPings.forEach((el) => {
        if (!el.responseMs) {
          res += 1;
        }
      });
      return res;
    },

    avgPingMs(state) {
      // go from oldest to most recent
      let pingTime = null;

      for (let ping_i = state.lastPings.length - 1; ping_i >= 0; ping_i-- ) {
        let ping = state.lastPings[ping_i];
        if (!ping.responseMs) {
          continue;
        }

        let rtt = ping.responseMs - ping.sentMs;
        if (pingTime === null) {
          pingTime = rtt;
          continue;
        }

        // update with 90% weight on new info
        pingTime = pingTime*0.1 + rtt*0.9;
      }

      if (pingTime === null) {
        // no info yet
        return null;
      }

      // round to int
      return Math.round(pingTime);
    }
  },
  mutations: {
    invalidateAccountCache(state) {
      console.log("invalidating account cache");
      state.sortedChatAccountsCache.recomputeNow = true;
    },
    toggleDebug(state) {
      state.debug = !state.debug;
      this.commit("invalidateAccountCache");
    },
    togglePreview(state) {
      state.testPreview = !state.testPreview;
      this.commit("invalidateAccountCache");
    },
    toggleTestWarnings(state) {
      state.testWarnings = !state.testWarnings;
      this.commit("invalidateAccountCache");
    },
    markUserInactive(state) {
      state.userInactive = true;
    },

    // dc handling
    setIsDisconnected(state, isDisconnected) {
      state.isDisconnected = isDisconnected;
      console.log("isDisconnected is now", state.isDisconnected);
    },

    // ! AUTH
    authenticate(state, user) {
      // console.log('user:', user);
      state.user = user;
    },
    logout(state) {
      state.user = {};
    },
    autoFocus(state, focus) {
      state.canAutoFocus = Boolean(focus);
    },
    "SOCKET_webapp_ping_back"(state, payload) {
      state.lastPings.forEach((el) => {
        if (el.id === payload.pingId) {
          el.responseMs = Date.now();
        }
      })

      state.userPingData = [...payload.ping_data];
    },
    "SOCKET_suggestions2"(state, payload) {
      for (let accountSuggestions of payload.account_suggestions) {
        let oldSuggestions = state.chatSuggestions2[accountSuggestions.displayID];
        let newSuggestions = accountSuggestions.suggestions;

        // update but only if not different
        if (JSON.stringify(oldSuggestions) !== JSON.stringify(newSuggestions)) {
          Vue.set(state.chatSuggestions2, accountSuggestions.displayID, newSuggestions);
        }
      }
    },
    "SOCKET_chatbot_suggestions"(state, payload) {
      for (let accountSuggestions of payload.account_suggestions) {
        let oldSuggestions = state.chatbotSuggestions[accountSuggestions.displayID];
        let newSuggestions = accountSuggestions.suggestions;

        // update but only if not different
        if (JSON.stringify(oldSuggestions) !== JSON.stringify(newSuggestions)) {
          Vue.set(state.chatbotSuggestions, accountSuggestions.displayID, newSuggestions);
        }
      }
    },

    "SOCKET_chat_sent"(state, payload) {
      let name = payload.displayID;
      let pendingMessages = state.pendingMessages[name] || [];

      // update pending status
      pendingMessages = pendingMessages.map((msg) => {
        if (msg.text === payload.message) {
          msg.status = 'sent';
          msg.status_change_ts = Date.now();
          msg.status_change_server_ts = payload.ts;
        }
        return msg;
      });

      state.pendingMessages[name] = pendingMessages;
      state.pendingMessages = {...state.pendingMessages};
    },

    "SOCKET_chat_msg"(state, payload) {
      let name = payload.displayID;
      var messages = [];
      if (name in state.messages) {
        messages = state.messages[name];
      }

      let pendingMessages = state.pendingMessages[name] || [];

      if (payload.type === 'warning') {
        let msg = {
          'displayID': payload.displayID,
          'ts': getTime(new Date()),
          'msg': payload.data.text,
          'type': 'warning',
        };
        addWarningMessage(state, msg);
      }

      if (payload.type === 'handend') {
        this.dispatch("handleHandEnded", {
          displayID: payload.displayID,
          roomId: payload.data.roomId,
          handNum: payload.data.hand_num,
        })
      }

      let added = addMessage(state.seenMessageIds, messages, payload);
      handlePendingOnAddMessage(pendingMessages, payload, name);

      if (!['warning', 'suggestion', 'game_event', 'handend'].includes(payload.type) && !payload.data.read && added) {
        let n = name in state.numUnread ? state.numUnread[name] : 0;
        state.numUnread[name] = n+1;
        state.numUnread = {...state.numUnread};
      }

      // keep only the last hour
      let last_hour = new Date(new Date() - 3600 * 1000);
      messages = messages.filter((m) => {
        return m.ts > last_hour;
      });

      // keep only the last 300 per chat
      messages = messages.slice(-300);

      state.messages[name] = messages;
      state.messages = {...state.messages};
      state.pendingMessages[name] = pendingMessages;
      state.pendingMessages = {...state.pendingMessages};
      state.chatAccountsLive[name] = makeChatAccount(state, name);
      state.chatAccountsLive = {...state.chatAccountsLive};

      if (!state.activeChatAccountName) {
        console.log("auto-pick from chat_msg with no active account");
        this.commit("autoPickActiveChatAccount");
      }
    },
    "SOCKET_chat_msgs_bulk"(state, data) {
      for (const name in data) {
        let msgs = data[name];
        handle_chat_msgs(state, name, msgs);
      }
      if (!state.activeChatAccountName) {
        console.log("auto-pick from chat_msgs_bulk with no active account");
        this.commit("autoPickActiveChatAccount");
      }
    },
    async "SOCKET_bot_states"(state, data) {
      state.nBotStateBatchesProcessing++;
      console.log("trigger bot_state... processing", state.nBotStateBatchesProcessing, "batches");

      try {
        // fetch('data:image/jpeg;base64,' + data.bot_states).then((data) => {
        //   data.arrayBuffer().then((data) => {
        //   data = new Uint8Array(data);

        // * to get opp info from club_mgmt
        let newOpponentCodes = [];

        data = new Uint8Array(Buffer.from(data.bot_states, 'base64'));
        let uncompressed = pako.inflate(data, {to: 'string'});

        let bot_states = JSON.parse(uncompressed);
        for (const data of bot_states) {
          updateBotState(state, data);

          // * get new opp codes
          if (data.state && data.state.players) {
            for (let k in data.state.players) {
              let player = data.state.players[k];

              // * ignore if loaded
              if (player.code in state.opponents || state.opponents[player.code]) {
                continue;
              }

              newOpponentCodes.push(player.code);
            }
          }
        }
        state.chatAccountsLive = {...state.chatAccountsLive};
        state.states = {...state.states};

        // load the new opp codes
        if (newOpponentCodes.length > 0) {
          this.dispatch("getOpponents", newOpponentCodes);
        }
      } finally {
        // no longer processing this batch
        state.nBotStateBatchesProcessing--;
      }
    },
    "SOCKET_accounts"(state, data) {
      let online = {};
      let update_chat_accounts = false;
      let got_new_account = false;
      let newDisplayIds = [];
      for (let acc of data.accounts) {


        // filter for limited user
        if (this.getters.isLimited && acc.app != 'Pkr2') {
          continue;
        }


        online[acc.displayID] = acc;

        online[acc.displayID].break = acc.displayID in state.onlineAccounts ? state.onlineAccounts[acc.displayID].break : false;

        // only trigger chat account recompute if we have in-game ones
        if (acc.status === 'in game') {
          update_chat_accounts = true;
        }

        if (!(acc.displayID in state.clubmgmAccountsAll.byNickname) && acc.status === 'in game') {
          got_new_account = true;
          newDisplayIds.push(acc.displayID);
        }

        if (acc.marked_read) {
          state.markerTs[acc.displayID] = new Date(acc.marked_read * 1000 - 1)
        }
      }

      // update accounts with this batch
      for (let displayId in online) {
        Vue.set(state.onlineAccounts, displayId, online[displayId]);
        state.chatAccountsLive[displayId] = makeChatAccount(state, displayId);
      }

      // trigger reactivity of accounts itself
      state.onlineAccounts = {...state.onlineAccounts};

      if (update_chat_accounts) {
        state.chatAccountsLive = {...state.chatAccountsLive};
      }

      if (got_new_account) {
        console.log("Got a new account! But won't load new ones yet...", newDisplayIds);
        state.lastNewAccountMs = Date.now();
      }

      state.markerTs = {...state.markerTs};
    },
    "SOCKET_accounts_automation_state"(state, data) {
      for (let acc of data.accounts) {

        // filter for limited user
        if (this.getters.isLimited && acc.app !== 'Pkr2') {
          continue;
        }

        let as = acc.automation_state;

        state.accounts_automation_state[acc.displayID] = as;
        Vue.set(state.accounts_automation_state, acc.displayID, as);

        // update on chatAcc also
        let chatAcc = state.chatAccountsLive[acc.displayID];
        if (chatAcc) {
          Vue.set(chatAcc, 'automationState', as);
          Vue.set(state.chatAccountsLive, acc.displayID, chatAcc);
        }
      }
    },
    "SOCKET_groups"(state, data) {
      state.schedulerGroups = data.groups;
    },
    "SOCKET_toggle_snoozed"(state, data) {
      state.onlineAccounts[data.displayID].snoozed = data.snoozed;
      state.chatAccountsLive[data.displayID].snoozed = data.snoozed;
      state.onlineAccounts = {...state.onlineAccounts};
      state.chatAccountsLive = {...state.chatAccountsLive};

      // update side-bar right away
      this.commit("invalidateAccountCache");
    },
    "SOCKET_toggle_priming"(state, data) {
      state.onlineAccounts[data.displayID].priming = data.priming;
      state.onlineAccounts[data.displayID].priming_user_id = data.priming_user_id;
      state.chatAccountsLive[data.displayID].priming = data.priming;
      state.chatAccountsLive[data.displayID].priming_user_id = data.priming_user_id;
      state.onlineAccounts = {...state.onlineAccounts};
      state.chatAccountsLive = {...state.chatAccountsLive};

      // update side-bar right away
      this.commit("invalidateAccountCache");
    },

    "SOCKET_toggle_typing"(state, data) {
      let name = data.displayID;
      state.onlineAccounts[name].is_typing = data.is_typing;
      state.chatAccountsLive[name].is_typing = data.is_typing;
      state.onlineAccounts = {...state.onlineAccounts};
      state.chatAccountsLive = {...state.chatAccountsLive};
    },

    "SOCKET_marked_needs_help"(state, data) {
      let name = data.displayID;
      state.onlineAccounts[name].operator_needs_help = data.operator_needs_help;
      state.chatAccountsLive[name].operator_needs_help = data.operator_needs_help;
      state.onlineAccounts = {...state.onlineAccounts};
      state.chatAccountsLive = {...state.chatAccountsLive};
    },

    "SOCKET_contact_host"(state, data) {
      state.onlineAccounts[data.displayID].contacting_host = data.contacting_host;
      state.onlineAccounts[data.displayID].contacting_host_users = data.contacting_host_users || {};
      state.onlineAccounts[data.displayID].contacting_host_not_available = false;
      state.chatAccountsLive[data.displayID].contacting_host = data.contacting_host;
      state.chatAccountsLive[data.displayID].contacting_host_users = data.contacting_host_users || {};
      state.chatAccountsLive[data.displayID].contacting_host_not_available = false;
      state.onlineAccounts = {...state.onlineAccounts};
      state.chatAccountsLive = {...state.chatAccountsLive};

      // got contact host, ping managers
      if (data.contacting_host_users && state.user && state.user._id in data.contacting_host_users) {
        this.dispatch('playAudio');
      }

      // update side-bar right away
      this.commit("invalidateAccountCache");
    },
    "SOCKET_buyin_rejected"(state, data) {
      state.onlineAccounts[data.displayID].buyin_state = data.buyin_state || {};
      state.chatAccountsLive[data.displayID].buyin_state = data.buyin_state || {};
      state.onlineAccounts = {...state.onlineAccounts};
      state.chatAccountsLive = {...state.chatAccountsLive};

      // got contact host, ping managers
      if (data.buyin_state && data.buyin_state.contacting_users && state.user && state.user._id in data.buyin_state.contacting_users) {
        this.dispatch('playAudio');
      }

      // update side-bar right away
      this.commit("invalidateAccountCache");
    },
    "SOCKET_toggle_claimed"(state, data) {
      state.onlineAccounts[data.displayID].claimed = data.claimed;
      state.onlineAccounts[data.displayID].claimed_user_id = data.claimed_user_id;
      state.chatAccountsLive[data.displayID].claimed = data.claimed;
      state.chatAccountsLive[data.displayID].claimed_user_id = data.claimed_user_id;
      state.onlineAccounts = {...state.onlineAccounts};
      state.chatAccountsLive = {...state.chatAccountsLive};

      // update side-bar right away
      this.commit("invalidateAccountCache");
    },
    "SOCKET_takebreak"(state, data) {
      state.onlineAccounts[data.displayID].break = data.break;
      let aca = state.chatAccountsLive[data.displayID];
      if (aca) {
        aca.break = data.break;
      }
    },
    "SOCKET_clubs"(state, data) {
      state.clubs[data.displayID] = data.clubs;
      state.clubs = {...state.clubs};
    },
    "SOCKET_rooms"(state, data) {
      state.rooms[data.displayID] = data.rooms;
      state.rooms = {...state.rooms};
    },
    "SOCKET_users"(state, data) {
      state.users = data.users;
      state.users = {...state.users};
    },
    "SOCKET_hello_rooms"(state, data) {
      for (const name in data) {
        let rooms = data[name];
        state.rooms[name] = rooms;
      }
      state.rooms = {...state.rooms};
    },
    "SOCKET_hello_clubs"(state, data) {
      for (const name in data) {
        let clubs = data[name];
        state.clubs[name] = clubs;
      }
      state.clubs = {...state.clubs};
    },
    "SOCKET_onduty"(state, data) {
      state.usersOnDuty = data.users_on_duty;
    },
    "SOCKET_user"(state, data) {
      state.user = data.user;
    },
    "SOCKET_marked_read"(state, data) {
      let name = data.displayID;
      let ts = new Date(data.ts * 1000-100);
      let msgs = state.messages[name];
      let new_messages = [];
      let marked = false;
      let marker = {"name": '', "text": '', "marker": true, "ts": ts, "read": true}
      let num_unread = 0;
      for (var msg of msgs) {
        if (msg.marker || msg.type === 'marker') {
          continue;
        }
        if (msg.ts <= ts) {
          msg.read = true;
        } else if (!marked) {
          marked = true;
          new_messages.push(marker);
          // console.log("adding marker on mark read, last message before ts found", name);
        }
        if (!['warning', 'suggestion', 'game_event', 'handend'].includes(msg.type) && !msg.read) {
          num_unread++;
        }
        new_messages.push(msg);
      }
      if (!marked) {
        // console.log("adding marker on mark read - no marker found", name);
        new_messages.push(marker);
      }

      state.markerTs[name] = ts;
      state.markerTs = {...state.markerTs};
      state.numUnreadTg[name] = 0;
      state.numUnreadTg = {...state.numUnreadTg};

      state.messages[name] = new_messages;
      state.numUnread[name] = num_unread;
      state.numUnread = {...state.numUnread};
      state.messages = {...state.messages};
      state.chatAccountsLive[name] = makeChatAccount(state, name);
      state.chatAccountsLive = {...state.chatAccountsLive};
    },

    // message batching!
    "SOCKET__messages"(state, data) {
      for (let msg of data.data) {
        for (let msg_name in msg) {
          let msg_data = msg[msg_name];
          this.commit('SOCKET_' + msg_name, msg_data);
        }
      }

      // got a batch emit, check if can fail any pending message
      // only do it if is_final batch
      if (data.is_final) {
        handlePendingOnBatch(state, data.ts)
      }
    },

    setActiveChatAccount(state, name) {
      state.activeChatAccountName = name;
    },

    autoPickActiveChatAccount(state) {
      // pick the next chat account
      state.activeChatAccountName = nextChatAccount(state, this.getters);
    },

    markChatRead(state, name) {
      let messages = (name in state.messages) ? state.messages[name] : [];
      let newMessages = [];
      let ts = 0;

      for (let msg of messages) {
        msg.read = true;
        if (msg.marker) continue;
        newMessages.push(msg);

        // get highest ts from messages
        if (msg.ts > ts) {
          ts = msg.ts;
        }
      }
      newMessages.push({name: '', text: '', marker: true, ts: new Date(), read: true})
      state.messages[name] = newMessages;
      state.messages = {...state.messages};

      // ! save read messages
      this.dispatch("markMessagesRead", {
        name: name,
        ts: ts / 1000
      });
      this.dispatch("postHeartbeat");

      // ! clear unread on this one
      state.chatAccountsLive[name].unread = 0;

      this.commit("invalidateAccountCache");
      console.log("auto-pick from mark read");
      this.commit("autoPickActiveChatAccount");
    },
    setClubmgmAccounts(state, clubmgmAccounts) {
      let accMap = {};
      let accIdMap = {};
      for (let clubmgmAcc of clubmgmAccounts) {
        if (!(clubmgmAcc.nickname in accMap)) {
          accMap[clubmgmAcc.nickname] = []
        }
        accMap[clubmgmAcc.nickname].push(clubmgmAcc);
        if (clubmgmAcc.pkr2_userId) {
          if (!(clubmgmAcc.pkr2_userId in accIdMap)) {
            accIdMap[clubmgmAcc.pkr2_userId] = [];
          }
          accIdMap[clubmgmAcc.pkr2_userId].push(clubmgmAcc);
        }

        // ! set break value
        let key = clubmgmAcc.nickname;
        let chatAccount = state.chatAccountsLive[key];
        if (chatAccount && clubmgmAcc.pkr2_id === chatAccount.clubID) {
          state.onlineAccounts[clubmgmAcc.nickname].break = clubmgmAcc.break;
        }

        // ! set agents and identities
        for (let agent of (clubmgmAcc.club || {}).agents || []) {
          state.agents[agent._id] = agent;
        }
        if (clubmgmAcc.identity) {
          state.identities[clubmgmAcc.identity._id] = clubmgmAcc.identity;
        }
      }
      state.clubmgmAccountsAll.byNickname = accMap;
      state.clubmgmAccountsAll.byId = accIdMap;
    },
    addClubmgmAccount(state, clubmgmAcc) {
      let nickname = clubmgmAcc.nickname;
      let accId = clubmgmAcc.pkr2_userId;

      if (!(nickname in state.clubmgmAccountsAll.byNickname))
        state.clubmgmAccountsAll.byNickname[nickname] = [clubmgmAcc];
      if (!(nickname in state.clubmgmAccountsAll.byId))
        state.clubmgmAccountsAll.byId[accId] = [clubmgmAcc];
    },
    toggleOnDuty(state, type) {
      // console.log(state.user)
      if (!state.user) {
        return;
      }
      this._vm.$socket.emit("toggle_on_duty", {'user': state.user.user, 'type': type});
    },
    setFollowMostRecent(state, data) {
      let displayID = data.displayID;
      let hh = state.pokerHandHistory[displayID];
      if (!hh) {
        hh = {
          currently_viewing: null,
          follow_most_recent: data.followMostRecent,
        };
      } else {
        // update most recent
        hh.follow_most_recent = data.followMostRecent;
      }

      if (data.followMostRecent) {
        // reload latest
        let acc = state.chatAccountsLive[displayID];
        let st = state.states[displayID];
        if (st && acc) {
          let roomId = st.room_id;
          let gt = acc.gameType;
          this.dispatch("loadHand", {displayID: displayID, roomId: roomId, gameType: gt, handNum: -1});
        }
      }

      // trigger state updates
      state.pokerHandHistory[displayID] = hh;
      state.pokerHandHistory = {...state.pokerHandHistory};
    },
  },
  actions: {
    ms100Ticked({ state, dispatch }) {
      // tick this check, which will cause chatAccounts getter to be triggered, which will then possibly
      // recompute the sort
      state.sortedChatAccountsCache.recomputeCheckSeconds += 0.1;

      dispatch('handlePings');

      let nowMs = Date.now();
      if (state.lastNewAccountMs > 0 && nowMs - state.lastNewAccountMs >= 5000) {
        console.log("Got a new account >5 seconds ago, will dispatch now");
        state.lastNewAccountMs = 0;
        dispatch("getClubAccounts");
      }
    },

    // channel handling
    subscribeTo({state}, payload) {
      this._vm.$socket.emit("webapp_subscribe", {'channel': payload.channel});
      if (state.subscribedChannels.indexOf(payload.channel) === -1) {
        state.subscribedChannels.push(payload.channel);
      }
      console.log("Subscribed to channel", payload.channel, "now in channels", state.subscribedChannels);
    },
    unsubscribeFrom({state}, payload) {
      this._vm.$socket.emit("webapp_unsubscribe", {'channel': payload.channel});
      state.subscribedChannels = state.subscribedChannels.filter((el) => { return el !== payload.channel });
      console.log("Unsubscribed from channel", payload.channel, "now in channels", state.subscribedChannels);
    },

    resetPings({state}) {
      state.lastPings = [];
      state.startedMs = Date.now();
    },

    handlePings({state}) {
      // keep only most recent 20 pings
      if (state.lastPings.length > 20) {
        state.lastPings.length = 20;
      }

      // see if we need ping, and get ping if we do

      if (needsPing(state)) {
        // insert the ping
        let pingId = state.lastPings.length > 0 ? state.lastPings[0].id + 1 : 0;
        let nowMs = Date.now();
        state.lastPings.splice(0, 0, {
          id: pingId,
          sentMs: nowMs,
        });

        // send the request
        this._vm.$socket.emit("webapp_ping", {
          "pingId": pingId,
          "avgPingMs": this.getters.avgPingMs
        });
      }
    },

    // ! AUTH
    async refresh({ commit }) {
      return axios.get("/api/refresh").then(res => {
        const data = res.data;
        // console.log(data);
        if (data.success) {
          commit("authenticate", data.data.user);
        } else {
          commit("logout");
        }
      }).catch(() => {
        commit("logout");
      });
    },
    async logout({ commit }) {
      return axios.post("/api/logout").then(() => {
        commit("logout");
        router.push({ name: "Login" });
      }).catch(() => {
        commit("logout");
        router.push({ name: "Login" });
      })
    },
    getClubAccounts({ commit }) {
      return axios.get("/api/accounts").then(
        (data) => {
          data = data.data;
          if (!data.success) {
            // console.log('no success')
            return false;
          }
          if (!data.accounts) {
            // console.log('no accounts')
            return false;
          }
          let accounts = data.accounts;
          commit('setClubmgmAccounts', accounts);
        }
      );
    },
    markMessagesRead({ state }, data) {
      let name = data.name;
      let ts = data.ts;

      let room_id = state.states[name].room_id;
      this._vm.$socket.emit("mark_read", {"displayID": name, "room_id": room_id, "ts": ts});
    },
    postHeartbeat() {
      // deeper group holds heartbeat for everyone
      let group = 'deeper';
      this._vm.$socket.emit("heartbeat", {'group': group});
    },
    getOpponents({ state }, codes) {
      if (!codes) {
        return;
      }

      let query = "";
      for (let code of codes) {
        query += `${code},`;
      }

      let url = `/api/opponents?codes=${query}`;

      return axios.get(url).then(res => {
        const data = res.data;
        if (!data.success) {
          return false;
        }

        for (let opp of data.opponents) {
          state.opponents[opp["playerCode"]] = opp;
        }
      })
    },
    addToHandHistory({ state }, data) {
      let displayID = data.displayID;

      let hh = state.pokerHandHistory[displayID];
      if (!hh) {
        hh = {
          currently_viewing: data,
          follow_most_recent: true,
        };
      } else {
        // switch to be viewing this one
        hh.currently_viewing = data;
      }

      state.pokerHandHistory[displayID] = hh;
      state.pokerHandHistory = {...state.pokerHandHistory};
    },
    addToStates({ state }, data) {
      state.states[data.displayID] = data.state;
      state.states = {...state.states};
    },
    updateScheduler({ state }, data) {
      let group = data["group"];

      axios.post(`/api/scheduler-settings`, data).then(res => {
        const d = res.data;

        // TODO special case
        if (group === "all") {
          for (let k in state.schedulerGroups) {
            state.schedulerGroups[k].off = data["off"];
          }
        } else {
          state.schedulerGroups[group].off = data["off"];
        }

        if (!d.success) {
          // console.log('no success scheduler update')
          return false;
        }
      })
    },
    addDNP(data) {
      axios.post(`/api/dnp`, data).then((res) => {
        const d = res.data;

        if (!d.success) {
          // console.log("couldn't update");
          // console.log(state.activeChatAccountName)
          return false;
        }
      })
    },
    clearDNP(data) {
      axios.delete(`/api/dnp/${data.code}`).then((res) => {
        const d = res.data;

        if (!d.success) {
          // console.log("couldn't update");
          // console.log(state.activeChatAccountName)
          return false;
        }
      })
    },

    operatorNeedsHelp({ state }, data) {
      state.onlineAccounts[data].operator_needs_help = {
        _id: state.user._id,
        user: state.user.user,
      }
      state.chatAccountsLive[data].operator_needs_help = {
        _id: state.user._id,
        user: state.user.user,
      }

      state.onlineAccounts = {...state.onlineAccounts};
      state.chatAccountsLive = {...state.chatAccountsLive};
    },

    loadHand({ dispatch, state }, data) {
      if (data.displayID !== state.activeChatAccountName) {
        return Promise.resolve(null);
      }

      // note: hand of -1 means to load the most recent hand

      return axios.get(`/api/hands/${data.displayID}?room_id=${data.roomId}&hand=${data.handNum}&game_type=${data.gameType}`).then((res) => {
        const data = res.data;
        const handData = data.hand;

        if (handData) {
          // trigger relevant updates
          if (data.gameType !== "ofc") {
            dispatch("addToHandHistory", handData);
          } else {
            dispatch("addToStates", handData);
          }
        }
      });
    },

    handleHandEnded({state, dispatch, getters}, data) {
      // all we do is see if we should load the hand
      let displayID = data.displayID;
      let roomId = data.roomId;
      let thisHandNum = data.handNum;

      // we only do it for active chat accounts
      if (displayID !== state.activeChatAccountName) {
        return;
      }

      // see if we trigger loading a hand...
      let handToLoad = null;

      let hh = state.pokerHandHistory[displayID];
      if (!hh) {
        // initialize to this hand
        handToLoad = thisHandNum;
      } else {
        if (hh.follow_most_recent) {
          // follow most recent, so load this one
          handToLoad = thisHandNum;
        } else {
          // see if we update curr viewing one
          if (!hh.currently_viewing) {
            // nothing to view, load this one
            handToLoad = thisHandNum;
          } else {
            // only need to update if the current viewing one is missing its next hand
            if (!hh.currently_viewing.next_hand) {
              handToLoad = hh.currently_viewing.handNum;
            }
          }
        }
      }

      if (handToLoad !== null) {
        // load the relevant hand
        dispatch("loadHand", {
          displayID: displayID,
          roomId: roomId,
          gameType: getters.gameType(displayID),
          handNum: handToLoad,
        });
      }
    },

    playAudio() {
      setTimeout(() => {
        let audio = new Audio('/static/sound/ping.mp3');
        audio.play();
      }, 500);
    },

    addPendingMessage({state}, message) {
      let messages = state.pendingMessages[message.displayID] || [];
      messages.push(message);
      state.pendingMessages[message.displayID] = messages;
      state.pendingMessages = {...state.pendingMessages}
    }

  }
});

export default store;
